import { useState, useMemo, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { usePrefs } from "@/contexts/PrefsContext";
import { api } from "@/lib/api";
import { currentMonth } from "@/lib/format";
import CommandPalette from "@/components/CommandPalette";
import CopilotFab from "@/components/CopilotFab";
import BrandLogo from "@/components/BrandLogo";
import BottomNav from "@/components/layout/BottomNav";
import QuickAddMenu from "@/components/layout/QuickAddMenu";
import ThemeToggle from "@/components/layout/ThemeToggle";
import PrivacyShield from "@/components/security/PrivacyShield";
import SessionWatermark from "@/components/security/SessionWatermark";
import PasskeySetupPrompt from "@/components/PasskeySetupPrompt";
import SupportAccessBanner from "@/components/SupportAccessBanner";
import DemoWorkspaceBanner from "@/components/DemoWorkspaceBanner";
import WorkspaceSelector from "@/components/layout/WorkspaceSelector";
import AuthenticatedFooter from "@/components/layout/AuthenticatedFooter";
import TrustLegalMenu from "@/components/layout/TrustLegalMenu";
import NotificationBell from "@/components/NotificationBell";
import OnboardingNudge from "@/components/OnboardingNudge";
import ProductTour from "@/components/ProductTour";
import { MINIMAL_SIDEBAR, SIDEBAR_GROUPS, SUPER_ADMIN_GROUP } from "@/components/layout/navConfig";
import { useUiRedesignV2 } from "@/lib/uiRedesignV2";
import {
    LayoutDashboard,
    CreditCard,
    Receipt,
    FileText,
    Banknote,
    Menu,
    X,
    LogOut,
    Wallet,
    UserSquare2,
    Settings as SettingsIcon,
    Building2,
    DatabaseZap,
    Tag,
    Coins,
    ClipboardCheck,
    ShieldCheck,
    Search,
    Users,
    ScrollText,
    LineChart,
    Package,
    Clock,
    Home as HomeIcon,
    Car,
    Compass,
    Sparkles,
    Award,
    Wand2,
    RotateCcw,
    KeyRound,
    Repeat,
    Target,
    Globe,
    Plane,
    Activity,
    History,
    ShieldAlert,
    RefreshCcw,
    HelpCircle,
    Mail,
    Zap,
    ChevronDown,
    Users as UsersIcon,
    Upload,
    TrendingUp,
    ArrowUpFromLine,
    Smartphone,
    Gift,
    FolderLock,
    CheckSquare,
    BarChart3,
    Landmark,
    ShieldOff,
    ShoppingBag,
    FileBarChart,
    Eye,
    EyeOff,
} from "lucide-react";

/* ─────────────────────────────────────────────────────────────────
   PRIMARY NAV — see `navConfig.js`. 12 top-level customer sections
   + a separate Super Admin block, all role-filtered.
   ───────────────────────────────────────────────────────────────── */

// ─── Trial countdown chip (Phase 1 conversion nudge) ────────────────
function TrialChip() {
    const [tenant, setTenant] = useState(null);
    useEffect(() => {
        api.get("/my-tenant").then((r) => setTenant(r.data?.tenant)).catch(() => setTenant(null));
    }, []);
    if (!tenant || tenant.status !== "trial" || !tenant.trial_end) return null;
    const end = new Date(tenant.trial_end);
    const days = Math.max(0, Math.ceil((end - new Date()) / (1000 * 60 * 60 * 24)));
    const tone = days <= 3 ? "bg-rose-50 border-rose-200 text-rose-700"
                : days <= 7 ? "bg-amber-50 border-amber-200 text-amber-800"
                : "bg-blue-50 border-blue-200 text-blue-700";
    return (
        <Link
            to="/pricing"
            className={`hidden md:inline-flex items-center gap-1.5 px-3 py-1 rounded-full border ${tone} text-[12px] font-medium hover:opacity-90`}
            data-testid="trial-chip"
        >
            <Clock size={11} />
            {days === 0 ? "Trial ends today" : `Trial · ${days}d left`}
        </Link>
    );
}

const ROUTE_LABELS = {
    "": "Command Centre",
    "finance-review": "Action Center",
    "data-health": "Data Health",
    "statements/review": "OCR Review Queue",
    forex: "Live Forex",
    "super-admin": "Super Admin",
    pricing: "Pricing",
    copilot: "Finance Copilot",
    security: "Security Center",
    help: "Help & Guide",
    cards: "Credit Cards",
    "cards/new": "Add Card",
    expenses: "Expenses",
    statements: "Statements",
    payments: "Payments",
    reconciliation: "Reconciliation",
    "bank-accounts": "Bank Accounts",
    subscriptions: "Subscriptions",
    "credit-score": "Credit Score",
    timeline: "Life Timeline",
    journeys: "Journeys",
    "smart-inbox": "Smart Capture Inbox",
    assets: "Asset Registry",
    renewals: "Renewals",
    "home-hub": "Home Hub",
    vehicles: "Vehicle Hub",
    "health-score": "Health Score",
    milestones: "Rewards Milestones",
    investments: "Investments",
    "tax-centre": "Tax Centre",
    family: "Family",
    "hub/money": "Money",
    "hub/credit": "Credit",
    "hub/wealth": "Wealth",
    "hub/commitments": "Commitments",
    "hub/protection": "Protection",
    "hub/insights": "Insights",
    users: "Users & Roles",
    settings: "Admin Settings",
    "audit-trail": "Audit Trail",
    bills: "Recurring Bills",
    documents: "Documents",
    budgets: "Budgets",
    alerts: "Alerts",
    "monthly-closing": "Monthly Closing",
};

function Brand() {
    return (
        <Link to="/dashboard" className="flex items-center gap-2 min-w-0" data-testid="brand-link">
            <BrandLogo variant="light" height={28} />
        </Link>
    );
}

function useCrumb() {
    const loc = useLocation();
    return useMemo(() => {
        const parts = loc.pathname.split("/").filter(Boolean);
        if (parts.length === 0) return { label: ROUTE_LABELS[""], parent: null };
        const key = parts.join("/");
        if (ROUTE_LABELS[key]) return { label: ROUTE_LABELS[key], parent: null };
        if (parts[0] === "cards" && parts.length > 1) {
            return { label: parts[1] === "new" ? "Add Card" : "Card Details", parent: { to: "/cards", label: "Credit Cards" } };
        }
        if (parts[0] === "hub" && parts[1]) {
            return { label: ROUTE_LABELS[`hub/${parts[1]}`] || parts[1], parent: null };
        }
        return { label: ROUTE_LABELS[parts[0]] || parts[0], parent: null };
    }, [loc.pathname]);
}

export default function AppLayout() {
    const [open, setOpen] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState(() => {
        try {
            const stored = localStorage.getItem("vault.sidebar_expanded");
            return stored ? JSON.parse(stored) : {};
        } catch { return {}; }
    });
    useEffect(() => {
        try { localStorage.setItem("vault.sidebar_expanded", JSON.stringify(expandedGroups)); } catch { /* noop */ }
    }, [expandedGroups]);
    const toggleGroup = (id) => setExpandedGroups((s) => ({ ...s, [id]: !s[id] }));
    const { user, logout } = useAuth();
    const { prefs, updatePrefs } = usePrefs();
    const navigate = useNavigate();
    const crumb = useCrumb();

    const initials = user?.name
        ? user.name.split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase()
        : "?";

    const canSee = (roles) => !roles || roles.includes(user?.role);
    const canAddExpense = ["admin", "finance", "expense_user"].includes(user?.role);

    // Notification badge count now lives inside `<NotificationBell />` —
    // it polls `/api/notifications?unread_only=true` on its own.

    // Desktop shows the minimal 6-item sidebar (post-Concierge redesign).
    // The full accordion menu lives under `/tools` and inside the mobile
    // "More" drawer for power users. Super Admin block still renders when
    // the caller is a platform owner.
    const visibleGroups = useMemo(() => {
        if (!user) return [];
        return MINIMAL_SIDEBAR.filter((g) => canSee(g.roles));
    }, [user?.role]);

    // For the mobile drawer we still want the full accordion.
    const drawerGroups = useMemo(() => {
        if (!user) return [];
        return SIDEBAR_GROUPS
            .filter((g) => canSee(g.roles))
            .map((g) => g.children
                ? { ...g, children: g.children.filter((c) => canSee(c.roles)) }
                : g)
            .filter((g) => !g.children || g.children.length > 0);
    }, [user?.role]);

    const visibleSuperAdmin = useMemo(() => {
        // P0 Multi-Tenant Security — Platform-Admin split.
        // Only users explicitly stamped with `platform_admin=true` on the
        // backend can see SaaS-ops menu items (All Tenants, Trial Users,
        // Global Search, MRR, etc.). Regular tenant admins never see this.
        if (!user || user.platform_admin !== true) return null;
        if (!canSee(SUPER_ADMIN_GROUP.roles)) return null;
        const children = SUPER_ADMIN_GROUP.children.filter((c) => canSee(c.roles));
        return children.length ? { ...SUPER_ADMIN_GROUP, children } : null;
    }, [user?.role, user?.platform_admin]);

    // Date/month selector — for now a display of the current month
    const [monthPicker, setMonthPicker] = useState(currentMonth());
    useEffect(() => {
        try { localStorage.setItem("vault.month", monthPicker); } catch { /* noop */ }
    }, [monthPicker]);

    // VAULT 2.0 · MIDNIGHT LUXE shell — apply `v2-shell` body class when the
    // founder allowlist enables the redesign. Automatic cleanup on unmount /
    // when the flag flips OFF, so we never leave stale classes on <body>.
    const { enabled: v2ShellEnabled } = useUiRedesignV2();
    useEffect(() => {
        if (!v2ShellEnabled) return undefined;
        document.body.classList.add("v2-shell");
        return () => document.body.classList.remove("v2-shell");
    }, [v2ShellEnabled]);

    return (
        <div className="min-h-screen bg-stone-50 flex">
            {/* ═══════ SIDEBAR ═══════ */}
            <aside
                className={`sidebar-shell fixed lg:sticky top-0 z-40 h-screen w-64 shrink-0 flex flex-col ${
                    open ? "translate-x-0" : "-translate-x-full"
                } lg:translate-x-0 transition-transform`}
                data-testid="app-sidebar"
            >
                <div className="px-5 py-5 flex items-center justify-between border-b border-stone-800/60">
                    <Brand />
                    <button
                        className="lg:hidden text-stone-400"
                        onClick={() => setOpen(false)}
                        data-testid="sidebar-close-btn"
                        aria-label="Close menu"
                    >
                        <X size={18} />
                    </button>
                </div>

                <nav className="flex-1 overflow-y-auto thin-scroll py-4 px-2 space-y-0.5" data-testid="sidebar-nav">
                    {/* ── Minimal sidebar on desktop; mobile drawer shows full accordion. ── */}
                    {visibleGroups.map((g) => (
                        <NavLink
                            key={g.id}
                            to={g.to}
                            end={g.to === "/"}
                            className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                            onClick={() => setOpen(false)}
                            data-testid={`nav-${g.id}`}
                        >
                            <g.icon size={16} strokeWidth={1.6} />
                            <span className="flex-1 truncate">{g.label}</span>
                        </NavLink>
                    ))}

                    {/* Mobile drawer: full categorised menu below the minimal set. */}
                    <div className="lg:hidden pt-4 mt-4 border-t border-stone-800/60">
                        <div className="eyebrow px-3 pb-2 text-stone-500">Everything else</div>
                        {drawerGroups.map((g) => {
                            if (!g.children) return null;
                            const expanded = !!expandedGroups[g.id];
                            return (
                                <div key={g.id} className="pt-0.5">
                                    <button
                                        onClick={() => toggleGroup(g.id)}
                                        data-testid={`nav-group-${g.id}`}
                                        className="sidebar-link w-full"
                                    >
                                        <g.icon size={16} strokeWidth={1.6} />
                                        <span className="flex-1 truncate text-left">{g.label}</span>
                                        <ChevronDown size={12} className={`transition-transform text-stone-500 ${expanded ? "" : "-rotate-90"}`} />
                                    </button>
                                    {expanded && (
                                        <div className="mt-0.5 mb-1 space-y-0.5">
                                            {g.children.map((c) => (
                                                <NavLink
                                                    key={`${g.id}-${c.to}`}
                                                    to={c.to}
                                                    className={({ isActive }) => `sidebar-link sidebar-link--sub ${isActive ? "active" : ""}`}
                                                    onClick={() => setOpen(false)}
                                                    data-testid={`nav-${g.id}-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                                >
                                                    <c.icon size={13} strokeWidth={1.5} />
                                                    <span className="flex-1 truncate">{c.label}</span>
                                                </NavLink>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* ── Super Admin (isolated from customer menu) ── */}
                    {visibleSuperAdmin && (
                        <div className="mt-4 pt-3 border-t border-stone-800/60">
                            <div className="eyebrow px-3 pt-1 pb-2 text-amber-500/80 flex items-center gap-1.5">
                                <ShieldAlert size={11} />
                                Platform Owner
                            </div>
                            <button
                                onClick={() => toggleGroup(visibleSuperAdmin.id)}
                                data-testid={`nav-group-${visibleSuperAdmin.id}`}
                                className="sidebar-link w-full"
                                aria-expanded={!!expandedGroups[visibleSuperAdmin.id]}
                            >
                                <visibleSuperAdmin.icon size={16} strokeWidth={1.6} />
                                <span className="flex-1 truncate text-left">{visibleSuperAdmin.label}</span>
                                <ChevronDown
                                    size={12}
                                    className={`transition-transform text-stone-500 ${expandedGroups[visibleSuperAdmin.id] ? "" : "-rotate-90"}`}
                                />
                            </button>
                            {expandedGroups[visibleSuperAdmin.id] && (
                                <div className="mt-0.5 space-y-0.5">
                                    {visibleSuperAdmin.children.map((c) => (
                                        <NavLink
                                            key={`super-${c.to}`}
                                            to={c.to}
                                            className={({ isActive }) => `sidebar-link sidebar-link--sub ${isActive ? "active" : ""}`}
                                            onClick={() => setOpen(false)}
                                            data-testid={`nav-super-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                        >
                                            <c.icon size={13} strokeWidth={1.5} />
                                            <span className="flex-1 truncate">{c.label}</span>
                                        </NavLink>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </nav>

                {/* Secondary nav removed — items moved into their proper
                    sections (Settings / Security Center / Super Admin). */}

                {/* User strip */}
                <div className="px-3 py-4 border-t border-stone-800/60">
                    <button
                        onClick={() => updatePrefs({ hide_balance: !prefs.hide_balance })}
                        className="mb-3 w-full flex items-center justify-center gap-2 py-2 rounded-md border border-stone-700 text-stone-300 hover:text-white hover:border-stone-500 text-sm transition-colors duration-200"
                        title={prefs.hide_balance ? "Show balances" : "Hide balances (blur every monetary value)"}
                        data-testid="hide-balance-toggle"
                    >
                        {prefs.hide_balance ? <EyeOff size={14} /> : <Eye size={14} />}
                        {prefs.hide_balance ? "Show balances" : "Hide balances"}
                    </button>
                    <div className="flex items-center gap-3 px-2">
                        <div className="w-9 h-9 rounded-full bg-stone-700 text-white flex items-center justify-center text-xs font-medium">
                            {initials}
                        </div>
                        <div className="min-w-0">
                            <div className="text-sm text-white truncate" data-testid="user-name">
                                {user?.name}
                            </div>
                            <div className="text-[11px] uppercase tracking-widest text-stone-500 truncate" data-testid="user-role-line">
                                {user?.title || user?.role}
                            </div>
                        </div>
                    </div>
                    <button
                        data-testid="logout-btn"
                        onClick={async () => { await logout(); navigate("/login"); }}
                        className="mt-4 w-full flex items-center justify-center gap-2 py-2 rounded-md border border-stone-700 text-stone-300 hover:text-white hover:border-stone-500 text-sm transition-colors duration-200"
                    >
                        <LogOut size={14} /> Sign out
                    </button>
                </div>
            </aside>

            {/* Backdrop */}
            {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

            {/* ═══════ MAIN ═══════ */}
            <div className="flex-1 flex flex-col min-w-0">
                <DemoWorkspaceBanner />
                <SupportAccessBanner />
                {/* Mobile header */}
                <header
                    className="lg:hidden sticky top-0 z-20 bg-white border-b border-stone-200 px-4 flex items-center justify-between safe-top"
                    style={{ minHeight: 52 }}
                >
                    <button onClick={() => setOpen(true)} data-testid="sidebar-open-btn" className="text-stone-700" aria-label="Open menu">
                        <Menu size={20} />
                    </button>
                    <Link to="/dashboard" className="flex items-center gap-2" data-testid="mobile-brand-link">
                        <BrandLogo variant="dark" height={22} />
                    </Link>
                    <QuickAddMenu canAdd={canAddExpense} />
                </header>

                {/* Desktop premium topbar — only 7 elements */}
                <div className="hidden lg:flex topbar-v3 sticky top-0 z-20 items-center gap-3 px-8 py-3 bg-white border-b border-stone-200">
                    {/* 1. Logo */}
                    <Link to="/dashboard" className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="crumb-home">
                        <BrandLogo variant="dark" height={20} />
                    </Link>

                    {/* Divider + breadcrumb (secondary context only) */}
                    <div className="hidden xl:flex items-center gap-2 pl-3 border-l border-stone-200 min-w-0">
                        <span className="text-sm text-stone-500 truncate" data-testid="crumb-current">
                            {crumb.label}
                        </span>
                    </div>

                    <div className="flex-1" />

                    {/* Workspace selector — My VAULT / VAULT Demo */}
                    <WorkspaceSelector />

                    {/* Trial-ends chip */}
                    <TrialChip />

                    {/* 2. Global search — v2 relabels as "Command VAULT" */}
                    <button
                        onClick={() => {
                            const ev = new KeyboardEvent("keydown", { key: "k", metaKey: true, bubbles: true });
                            document.dispatchEvent(ev);
                        }}
                        className="hidden md:inline-flex items-center gap-2 px-3 py-1.5 text-sm text-stone-500 border border-stone-200 rounded-full hover:border-stone-300 hover:text-stone-700 transition-colors"
                        data-testid="topbar-search-btn"
                    >
                        <Search size={14} /> {v2ShellEnabled ? "Command VAULT" : "Search"}
                        <span className="text-xs text-stone-400 ml-1">⌘K</span>
                    </button>

                    {/* 3. Date/Month selector */}
                    <input
                        type="month"
                        value={monthPicker}
                        onChange={(e) => setMonthPicker(e.target.value)}
                        className="hidden md:block px-3 py-1.5 text-sm border border-stone-200 rounded-full text-stone-700 hover:border-stone-300 bg-white focus:outline-none focus:border-stone-400"
                        data-testid="topbar-month-picker"
                        aria-label="Reporting period"
                    />

                    {/* 4. Owner/entity selector */}
                    <select
                        defaultValue="all"
                        className="hidden lg:block px-3 py-1.5 text-sm border border-stone-200 rounded-full text-stone-700 hover:border-stone-300 bg-white focus:outline-none focus:border-stone-400"
                        data-testid="topbar-owner-selector"
                        aria-label="Owner / entity"
                    >
                        <option value="all">All owners</option>
                        <option value="personal">Personal</option>
                        <option value="business">Business</option>
                        <option value="family">Family</option>
                    </select>

                    {/* 5. Quick Add */}
                    <QuickAddMenu canAdd={canAddExpense} />

                    {/* 5b. Theme */}
                    <ThemeToggle />

                    {/* 5c. Trust & Legal */}
                    <TrustLegalMenu />

                    {/* 6. Notifications */}
                    <OnboardingNudge />
                    <NotificationBell />

                    {/* 7. User profile */}
                    <button
                        onClick={() => navigate("/security")}
                        className="inline-flex items-center gap-2 pr-3 pl-1 py-1 rounded-full border border-stone-200 hover:border-stone-300 transition-colors"
                        data-testid="topbar-profile-btn"
                    >
                        <span className="w-7 h-7 rounded-full bg-stone-900 text-white flex items-center justify-center text-[11px] font-semibold">
                            {initials}
                        </span>
                        <span className="hidden xl:block text-sm text-stone-700 max-w-[80px] truncate">{user?.name?.split(" ")[0]}</span>
                    </button>
                </div>

                <main className="flex-1 p-4 sm:p-6 lg:p-8 pb-24 md:pb-8 vault-authenticated" data-testid="authenticated-main">
                    <SessionWatermark />
                    <PasskeySetupPrompt />
                    <Outlet />
                </main>
                <AuthenticatedFooter />
            </div>
            <BottomNav onOpenMenu={() => setOpen(true)} />
            <CommandPalette />
            <CopilotFab />
            <PrivacyShield />
            <ProductTour />
        </div>
    );
}
